import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer, View, Views } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { startOfWeek, parse, format, getDay } from "date-fns";
import { enUS } from "date-fns/locale";
import { Box, CircularProgress, IconButton, Tooltip, Typography, ToggleButton, ToggleButtonGroup, Button } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import { CalendarEvent } from "../../hooks/useCalendarEvents";
import MeetingPreviewDialog from "./MeetingPreviewDialog";

const locales = { "en-US": enUS };

// Weeks run Mon–Sun here, matching the rest of the app (isoWeek everywhere else)
// and how advisors read their own calendar.
const startOfWeekMonday = (date: Date) => startOfWeek(date, { weekStartsOn: 1 });

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: startOfWeekMonday,
  getDay,
  locales,
});

export type CalendarViewMode = Extract<View, "month" | "week" | "day">;

export interface MeetingsCalendarProps {
  events: CalendarEvent[];
  view: CalendarViewMode;
  onViewChange: (view: CalendarViewMode) => void;
  date: Date;
  onDateChange: (date: Date) => void;
  /** Open the Prospo meeting behind an event. */
  onSelectMeeting: (meeting: any) => void;
  /** Promote a calendar event that has no Prospo record yet. */
  onAddToProspo: (event: CalendarEvent) => void;
  /** True while calendar/Zoom events are being refetched for a new date range. */
  loading?: boolean;
  /** externalId of the event currently being added, so its button can show progress. */
  addingId?: string | null;
  height?: number;
}

/** Grey for General, white for everything else — matches the Type badge in the list view. */
const isGeneral = (event: CalendarEvent) => event.category === "general";

/**
 * Needs a client created before it can be filed. Two sources: a discovered 1-1
 * whose counterparty isn't a client, and a Calendly booking that already exists
 * as a Prospo meeting but has no client attached (a stranger off the public
 * booking link) — so this deliberately does NOT exclude inProspo events.
 */
const needsClient = (event: CalendarEvent) => event.shape === "oneToOneUnknown";

/** "4:30 PM – 5:00 PM · Client review" — the hover-only detail (#2081). */
const eventTooltip = (event: CalendarEvent): string => {
  const time = `${format(event.start, "h:mm a")} – ${format(event.end, "h:mm a")}`;
  return `${time} · ${event.title}`;
};

const EventChip: React.FC<{
  event: CalendarEvent;
  onAddToProspo: (event: CalendarEvent) => void;
  addingId?: string | null;
  /** Month cells are narrow, so the button drops its label there. */
  compact?: boolean;
}> = ({ event, onAddToProspo, addingId, compact }) => {
  const general = isGeneral(event);
  const pending = needsClient(event);
  const adding = addingId === event.id;

  // Yellow outranks the category colour: "this needs a client created" is more
  // actionable than General-vs-Advice, and the chip has one chance to say so.
  const palette = pending
    ? { border: "1px solid #fcd34d", background: "#fffbeb", color: "#92400e" }
    : general
      ? { border: "1px solid #d1d5db", background: "#f3f4f6", color: "#374151" }
      : { border: "1px solid #bae6fd", background: "#ffffff", color: "#0369a1" };

  return (
    <Tooltip
      title={
        pending
          ? `${eventTooltip(event)} — ${event.counterparty?.name || "this person"} is not a Prospo client yet`
          : eventTooltip(event)
      }
      placement="top"
      arrow
      enterDelay={200}
    >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        width: "100%",
        // Fill the slot RBC gives us so side-by-side clashes stay aligned.
        height: "100%",
        minWidth: 0,
        overflow: "hidden",
        px: "6px",
        py: "2px",
        borderRadius: "6px",
        border: palette.border,
        backgroundColor: palette.background,
        color: palette.color,
        fontSize: "0.75rem",
        lineHeight: 1.4,
        boxSizing: "border-box",
      }}
    >
      <Box
        component="span"
        sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {event.title}
      </Box>

      {/* Not yet in Prospo, or in Prospo but with no client to file it under. */}
      {(!event.inProspo || pending) && (
        // No nested Tooltip here — the chip's own hover tooltip covers the whole
        // event, and two overlapping tooltips flicker against each other.
        <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
          <Button
            size="small"
            disabled={adding}
            onClick={(e) => {
              // The calendar cell would otherwise treat this as selecting the event.
              e.stopPropagation();
              onAddToProspo(event);
            }}
            startIcon={compact ? undefined : <AddIcon sx={{ fontSize: "0.85rem !important" }} />}
            aria-label={
              pending ? "Create client and add this meeting to Prospo" : "Add this meeting to Prospo"
            }
            sx={{
              minWidth: 0,
              px: compact ? "3px" : "6px",
              py: 0,
              fontSize: "0.68rem",
              fontWeight: 600,
              lineHeight: 1.5,
              textTransform: "none",
              color: pending ? "#92400e" : "#1d4ed8",
              backgroundColor: pending ? "#fef3c7" : "#eff6ff",
              border: pending ? "1px solid #fcd34d" : "1px solid #bfdbfe",
              "& .MuiButton-startIcon": { mr: "2px", ml: 0 },
              "&:hover": { backgroundColor: pending ? "#fde68a" : "#dbeafe" },
            }}
          >
            {compact ? <AddIcon sx={{ fontSize: "0.9rem" }} /> : adding ? "Adding…" : "Add"}
          </Button>
        </Box>
      )}
    </Box>
    </Tooltip>
  );
};

/** The period label above the grid — RBC's own toolbar is replaced, so derive it here. */
const periodLabel = (date: Date, view: CalendarViewMode): string => {
  if (view === "month") return format(date, "MMMM yyyy");
  if (view === "day") return format(date, "EEEE d MMMM yyyy");
  const weekStart = startOfWeekMonday(date);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  const sameMonth = weekStart.getMonth() === weekEnd.getMonth();
  return sameMonth
    ? `${format(weekStart, "d")} – ${format(weekEnd, "d MMM yyyy")}`
    : `${format(weekStart, "d MMM")} – ${format(weekEnd, "d MMM yyyy")}`;
};

const MeetingsCalendar: React.FC<MeetingsCalendarProps> = ({
  events,
  view,
  onViewChange,
  date,
  onDateChange,
  loading = false,
  onSelectMeeting,
  onAddToProspo,
  addingId,
  height,
}) => {
  const label = useMemo(() => periodLabel(date, view), [date, view]);
  const [previewEvent, setPreviewEvent] = useState<CalendarEvent | null>(null);

  // Week/day views open at midnight otherwise, so the working day starts offscreen.
  // 8am matches the advisor's typical business hours (still freely scrollable
  // to any hour — this only sets where the view lands on open).
  const scrollToTime = useMemo(() => {
    const d = new Date();
    d.setHours(8, 0, 0, 0);
    return d;
  }, []);

  // RBC's time-grid rows default to a fixed 40px each (react-big-calendar.css),
  // so the "default 8am–6pm window" isn't actually 8am–6pm — it's just
  // whatever number of 40px hours happens to fit in this panel's own height
  // (520px ÷ 40px = 13 hours → 8am–9pm). RBC also computes scrollToTime once
  // at mount against that fixed row height and never re-syncs it, which is
  // why zooming the browser (a resize) leaves the view "stuck" afterwards.
  // Measuring the actual container and setting the row height so exactly
  // VISIBLE_HOURS fill it — reapplied on every resize, zoom included — fixes
  // both: the default view is always 8am–6pm regardless of screen height,
  // and it never drifts after a resize.
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const VISIBLE_HOURS = 10; // 8am–6pm
    const START_HOUR = 8;

    const applySizing = () => {
      const content = el.querySelector<HTMLElement>(".rbc-time-content");
      const slotGroups = el.querySelectorAll<HTMLElement>(".rbc-timeslot-group");
      if (!content || slotGroups.length === 0) return; // month view has neither

      const perHourPx = content.clientHeight / VISIBLE_HOURS;
      slotGroups.forEach((group) => {
        // `height`, not just `min-height` — the gutter's own hour-label text
        // (e.g. "8:00am") can otherwise force ITS rows taller than the plain
        // day-column rows next to them, so the gutter silently runs out of
        // vertical room a row early and prints one fewer label than the grid
        // actually has space for (the reported 5pm-instead-of-6pm symptom).
        group.style.height = `${perHourPx}px`;
        group.style.minHeight = `${perHourPx}px`;
      });
      content.scrollTop = perHourPx * START_HOUR;
    };

    applySizing();
    const observer = new ResizeObserver(applySizing);
    observer.observe(el);
    return () => observer.disconnect();
  }, [view, date]);

  const components = useMemo(
    () => ({
      event: ({ event }: { event: CalendarEvent }) => (
        <EventChip
          event={event}
          onAddToProspo={onAddToProspo}
          addingId={addingId}
          compact={view === "month"}
        />
      ),
      // Navigation lives in the header above, so suppress RBC's built-in toolbar.
      toolbar: () => null,
    }),
    [onAddToProspo, addingId, view],
  );

  // Clicking any event opens the preview — including events not yet in Prospo,
  // which previously did nothing at all (#2082). The preview decides what
  // actions are available from there.
  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    setPreviewEvent(event);
  }, []);

  // RBC styles events itself; we render our own chip, so strip its defaults.
  const eventPropGetter = useCallback(
    () => ({
      style: {
        backgroundColor: "transparent",
        border: "none",
        padding: 0,
        margin: "1px 0",
        color: "inherit",
        boxShadow: "none",
      },
    }),
    [],
  );

  const navigate = (direction: -1 | 1) => {
    const unit = view === "month" ? "month" : view === "week" ? "week" : "day";
    const next = new Date(date);
    if (unit === "month") next.setMonth(next.getMonth() + direction);
    else if (unit === "week") next.setDate(next.getDate() + direction * 7);
    else next.setDate(next.getDate() + direction);
    onDateChange(next);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => navigate(-1)}
            aria-label="Previous period"
            sx={{ border: "1px solid #e5e7eb", borderRadius: "8px" }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Button
            size="small"
            onClick={() => onDateChange(new Date())}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#374151",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              px: 1.5,
            }}
          >
            Today
          </Button>
          <IconButton
            size="small"
            onClick={() => navigate(1)}
            aria-label="Next period"
            sx={{ border: "1px solid #e5e7eb", borderRadius: "8px" }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: "#111827", ml: 0.5 }}>
            {label}
          </Typography>
          {loading && (
            <CircularProgress size={14} thickness={5} sx={{ ml: 1, color: "#6b7280" }} />
          )}
        </Box>

        <ToggleButtonGroup
          value={view}
          exclusive
          size="small"
          onChange={(_, next) => next && onViewChange(next)}
          sx={{
            backgroundColor: "#f3f4f6",
            borderRadius: "8px",
            "& .MuiToggleButton-root": {
              textTransform: "none",
              px: 1.5,
              py: 0.25,
              fontSize: "0.8rem",
              border: "1px solid #e5e7eb",
              color: "#374151",
            },
            "& .Mui-selected": {
              backgroundColor: "#fff !important",
              color: "#111827",
              fontWeight: 600,
            },
          }}
        >
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <style>{`
        .prospo-calendar .rbc-event,
        .prospo-calendar .rbc-event:focus { outline: none; }
        .prospo-calendar .rbc-event.rbc-selected { background-color: transparent; }
        .prospo-calendar .rbc-today { background-color: #f0f9ff; }
        .prospo-calendar .rbc-off-range-bg { background-color: #fafafa; }
        .prospo-calendar .rbc-header {
          padding: 8px 4px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          border-bottom: 1px solid #e5e7eb;
        }
        .prospo-calendar .rbc-month-view,
        .prospo-calendar .rbc-time-view { border: 1px solid #e5e7eb; border-radius: 8px; }
        .prospo-calendar .rbc-allday-cell { display: none; }
        .prospo-calendar .rbc-show-more { font-size: 0.7rem; color: #1d4ed8; font-weight: 600; }

        /* ── Time labels are hover-only (#2081) ──────────────────────────
           RBC prints its own "4:30 PM – 5:00 PM" label above the custom chip.
           At a narrow width (two clashing events share one column) that fixed
           text overflows its box and collides with the neighbouring event, which
           is what made overlaps unreadable. The time now lives in the chip's
           hover tooltip instead, so the row only ever renders the title. */
        .prospo-calendar .rbc-event-label { display: none; }

        /* The chip supplies its own padding and border, so RBC's must go —
           otherwise each clashing event loses ~10px it can't spare. */
        .prospo-calendar .rbc-event { padding: 0 !important; border: none !important; }
        .prospo-calendar .rbc-event-content { width: 100%; min-width: 0; overflow: hidden; }

        /* Side-by-side clashes: a hairline gap so two adjacent chips read as
           two events rather than one wide box. */
        .prospo-calendar .rbc-time-view .rbc-event { box-shadow: none; }
        .prospo-calendar .rbc-day-slot .rbc-event { border-right: 2px solid transparent; background-clip: padding-box; }
      `}</style>

      {/* Fills the panel when it has a height to fill, falls back to a sensible
          fixed height on small screens where the panel is auto-height. */}
      <Box ref={containerRef} className="prospo-calendar" sx={{ height: height ?? { xs: 520, lg: "100%" }, minHeight: 420 }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          view={view}
          onView={(next: View) => onViewChange(next as CalendarViewMode)}
          views={[Views.MONTH, Views.WEEK, Views.DAY]}
          date={date}
          onNavigate={onDateChange}
          components={components}
          eventPropGetter={eventPropGetter}
          onSelectEvent={handleSelectEvent}
          scrollToTime={scrollToTime}
          // Clashing events tile side-by-side instead of RBC's default, which
          // overlaps them with a small x-offset and buries the one underneath (#2081).
          dayLayoutAlgorithm="no-overlap"
          popup
          style={{ height: "100%" }}
        />
      </Box>

      <MeetingPreviewDialog
        open={!!previewEvent}
        event={previewEvent}
        onClose={() => setPreviewEvent(null)}
        onEdit={(meeting) => {
          setPreviewEvent(null);
          onSelectMeeting(meeting);
        }}
        onAddToProspo={(event) => {
          setPreviewEvent(null);
          onAddToProspo(event);
        }}
      />
    </Box>
  );
};

export default MeetingsCalendar;
